// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { SkeletonLoader } from '../../components/SkeletonLoader.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { mfaStepUpPath } from '../../utils/mfa-step-up.js';

/**
 * Sprint 63.23.0 — the consent screen's session requirement, on its own.
 *
 * A signed-out visitor goes through the existing login with `?next=` naming
 * this exact URL, so SSO and MFA apply unchanged and the person comes straight
 * back (`ProtectedRoute` redirects to `/login` with no return path, which is
 * why the consent route does not use it). A session held at the MFA enrolment
 * interstitial goes there first, as `ProtectedRoute` does.
 *
 * Kept apart from `ConsentPage` deliberately: this component redirects on the
 * SESSION, which the app shell has already read, and makes no API read of its
 * own. The visual-acceptance passthrough census treats any module that both
 * reads the API and renders `<Navigate` as a route gate whose reads must reach
 * the real server; the consent request read decides nothing about routing, so
 * it lives in the page, and the redirect lives here.
 */
export function ConsentSessionGate({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <SkeletonLoader variant="card" count={1} />;
  const next = encodeURIComponent(`${location.pathname}${location.search}`);
  if (!isAuthenticated) return <Navigate to={`/login?next=${next}`} replace />;
  // Gate E12.5 pass 1 (P2-7): the interstitial is given the same return path.
  if (user?.mfa_enrolment_required) {
    return <Navigate to={`/mfa-enrolment-required?next=${next}`} replace />;
  }
  // Gate E12.5 pass 2: a session held at the TOTP step-up enters its code first.
  if (user?.mfa_required) {
    return <Navigate to={mfaStepUpPath(`${location.pathname}${location.search}`)} replace />;
  }
  return <>{children}</>;
}
